import * as duckdb from '@duckdb/duckdb-wasm';
import { useEffect, useState } from 'react';
import { useDuckDb } from '../third-party-kit/hooks/useDuckDb';
import { getTempFilename } from '../third-party-kit/util/tempfile';

/**
 * Table configuration for useLoadTable
 */
export type SupportedColumnTypes =
  | 'VARCHAR'
  | 'TIMESTAMP_S'
  | 'FLOAT'
  | 'INTEGER';

export type TableColumnDefinitions = {
  [columnName: string]: SupportedColumnTypes;
};

export type TableFileType = 'json' | 'parquet';

export interface TableConfig {
  columns: TableColumnDefinitions;
  /**
   * @default 'json'
   * The file type to use when loading the table.
   * If not specified, defaults to 'json'.
   */
  fileType?: TableFileType;
}

/**
 * Clear and load a duckDB table with the specified data and configuration.
 * Supports JSON and Parquet file types.
 * @param tableName name of the table
 * @param data array of data objects
 * @param config table configuration (columns, fileType)
 * @returns
 */
export const useLoadTable = <T = unknown>(
  tableName: string,
  data: T[] | null | undefined,
  config: TableConfig
) => {
  const [loading, setLoading] = useState(true);

  const { db } = useDuckDb();

  useEffect(() => {
    const loadTable = async (db: duckdb.AsyncDuckDB, data: T[]) => {
      const conn = await db.connect();

      try {
        await conn.query('BEGIN TRANSACTION;');

        await conn.query(`DROP TABLE IF EXISTS ${tableName};`);

        if (data.length) {
          const filename = `${getTempFilename()}.${config.fileType}`;

          if (config.fileType === 'json' || !config.fileType) {
            // Assume JSON if not specified
            await db.registerFileText(filename, JSON.stringify(data));

            await conn.insertJSONFromPath(filename, { name: tableName });
          } else if (config.fileType === 'parquet') {
            // Parquet writing not implemented here, placeholder for extension
            throw new Error(
              'Parquet fileType is not yet supported in useLoadTable.'
            );
          }
        } else {
          await conn.query(`
          CREATE TABLE ${tableName} (
            ${Object.keys(config.columns)
              .map(
                (columnName) => `"${columnName}" ${config.columns[columnName]}`
              )
              .join(', ')}
          );`);
        }
        await conn.query('COMMIT;');
      } catch (e) {
        // error is expected if the same table is being created by multiple hook invocations
        // one of them will create it and the rest will fail
        console.debug(
          `Did not create table '${tableName}' (expected if table already exists).`,
          e
        );
        await conn.query('ROLLBACK;');
      } finally {
        conn.close();
        setLoading(false);
      }
    };

    if (db && data) {
      setLoading(true);
      loadTable(db, data);
    }
  }, [tableName, db, data, config.fileType, config.columns]);

  return { isTableLoading: loading };
};
